import React, { useContext, useEffect, useState } from 'react';
import axios from 'axios';
import { useAlert } from 'react-alert';
import { useParams } from 'react-router-dom';
import AuthContext from '../../authentication/AuthContext';
import { Col, Row, Tab, Table, Tabs } from 'react-bootstrap';
import WebDealLine from './web-deal-line/WebDealLine';
import styles from './webDealLines.module.scss';
import UserContext from '../../user/UserContext';
import WebDealSubmission from './submit/WebDealSubmission';
import WebDealImagePlaceholder from './image-placeholder/WebDealImagePlaceholder';
import AttachedWebDealLine from './web-deal-line/AttachedWebDealLine';

export default function WebDealLines() {
    const cas = useContext(AuthContext);
    const { userDetails } = useContext(UserContext);

    const [dealLines, setDealLines] = useState([]);
    const [attachedDealLines, setAttachedDealLines] = useState([]);

    const [summary, setSummary] = useState();
    const [submitEnabled, setSubmitEnabled] = useState(false);

    const [tabKey, setTabKey] = useState('available');

    const alert = useAlert();
    const params = useParams();
    const dealId = params.dealId;

    useEffect(() => {
        getWebDealHeaderSummary();
        getWebDealLines();
        // eslint-disable-next-line
    }, [dealId, cas, userDetails]);

    const getWebDealHeaderSummary = () => {
        if (userDetails.currentFranchise) {
            axios
                .get(
                    window.CONFIG['ws.activoc-web-deals-public.header.summary']
                        .replace('{webDealHeaderId}', dealId)
                        .replace('{franchiseCode}', userDetails.currentFranchise.code),
                    {
                        headers: {
                            Authorization: cas,
                            'Content-Type': 'application/json'
                        }
                    }
                )
                .then(response => {
                    setSummary(response.data);
                })
                .catch(() => alert.error('ERROR: Unable to retrieve web deal header summary.'));
        }
    };

    const getWebDealLines = () => {
        if (userDetails.currentFranchise) {
            axios
                .get(
                    window.CONFIG['ws.activoc-web-deals-public.lines']
                        .replace('{headerId}', dealId)
                        .replace('{franchiseCode}', userDetails.currentFranchise.code),
                    {
                        headers: {
                            Authorization: cas,
                            'Content-Type': 'application/json'
                        }
                    }
                )
                .then(response => {
                    const returnedLines = response.data;

                    extractLinesWithoutOrder(returnedLines);
                    extractLinesWithOrder(returnedLines);

                    getWebDealHeaderSummary();
                })
                .catch(() => {
                    alert.error('ERROR: Unable to retrieve web deal lines.');
                    const errorMessage = (
                        <tr className={'error'}>
                            <td colSpan={12}>ERROR: Unable to retrieve web deal lines</td>
                        </tr>
                    );
                    setDealLines(errorMessage);
                });
        }
    };

    const extractLinesWithoutOrder = lines => {
        let submitEnabledTemp = false;
        const unattachedDealLines = lines
            .filter(line => line.orderAttached !== 'ATTACHED')
            .map(dealLine => {
                submitEnabledTemp |=
                    (dealLine.submitStatus === 'PENDING' && 0 < dealLine.currentQuantity) ||
                    ((dealLine.submitStatus === 'SUCCESS' || dealLine.submitStatus === 'FAIL') &&
                        dealLine.acceptedQuantity !== dealLine.currentQuantity);
                return (
                    <WebDealLine
                        dealLine={dealLine}
                        getWebDealLines={getWebDealLines}
                        key={dealLine.productCode}
                    />
                );
            });

        setSubmitEnabled(submitEnabledTemp);
        setDealLines(unattachedDealLines);
    };

    const extractLinesWithOrder = lines => {
        const attachedDealLines = lines
            .filter(line => line.orderAttached === 'ATTACHED')
            .map(dealLine => <AttachedWebDealLine dealLine={dealLine} key={dealLine.productCode} />);

        setAttachedDealLines(attachedDealLines);
    };

    return (
        <>
            <div className={'page-header'}>
                <div className={'content'}>
                    <div className={'order-header-container'}>
                        <div className={`${styles.webDealOrderHeader}`}>
                            {summary?.remainingStock < 100 && (
                                <div className={styles.textRibbonWrapper}>
                                    <span className={styles.textRibbon}>Ending Soon!</span>
                                </div>
                            )}
                            <Row className={`order-info`}>
                                <Col sm={8}>
                                    <h1>{summary?.webDealDescription}</h1>
                                    <div className={styles.webDealImage}>
                                        {summary?.imageUrl ? (
                                            <img src={summary?.imageUrl} alt={summary?.webDealDescription} />
                                        ) : (
                                            <WebDealImagePlaceholder />
                                        )}
                                    </div>
                                </Col>
                                <Col className={styles.totals} sm={4}>
                                    <p>
                                        <strong>Total lines: </strong> {summary?.totalLines}
                                    </p>
                                    <p>
                                        <strong>Total cases: </strong> {summary?.totalCases}
                                    </p>
                                    <p>
                                        <strong>Total cost: </strong>
                                    </p>
                                    <p className={styles.totalCost}>{formatTotalCost(summary?.totalCost)}</p>
                                    <WebDealSubmission
                                        webDealHeaderId={dealId}
                                        getWebDealLines={getWebDealLines}
                                        submitReady={submitEnabled}
                                    />
                                </Col>
                            </Row>
                        </div>
                    </div>
                </div>
            </div>
            <div className={`content order-lines ${styles.webDealLines}`}>
                <Tabs id={'web-deal-line-tabs'} activeKey={tabKey} onSelect={k => setTabKey(k)}>
                    <Tab eventKey={'available'} title={'Available lines'}>
                        <Table>
                            <thead>
                                <tr>
                                    <th>Image</th>
                                    <th>Code</th>
                                    <th className={styles.description}>Description</th>
                                    <th>
                                        Unit <br />
                                        Size
                                    </th>
                                    <th>
                                        Deal <br />
                                        Cost
                                    </th>
                                    <th>
                                        Deal <br />
                                        RSP
                                    </th>
                                    <th>Margin</th>
                                    <th>Min</th>
                                    <th>Max</th>
                                    <th>Multiple</th>
                                    <th>Stock</th>
                                    <th>
                                        Accepted <br />
                                        Quantity
                                    </th>
                                    <th>Quantity</th>
                                    <th>Status</th>
                                </tr>
                            </thead>
                            <tbody>{dealLines}</tbody>
                        </Table>
                    </Tab>
                    {attachedDealLines.length > 0 && (
                        <Tab eventKey={'attached'} title={`Attached to order (${attachedDealLines.length})`}>
                            <Table>
                                <thead>
                                    <tr>
                                        <th>Image</th>
                                        <th>Code</th>
                                        <th className={styles.description}>Description</th>
                                        <th>
                                            Unit <br />
                                            Size
                                        </th>
                                        <th>
                                            Deal <br />
                                            Cost
                                        </th>
                                        <th>
                                            Deal <br />
                                            RSP
                                        </th>
                                        <th>Margin</th>
                                        <th>
                                            Accepted <br />
                                            Quantity
                                        </th>
                                    </tr>
                                </thead>
                                <tbody>{attachedDealLines}</tbody>
                            </Table>
                        </Tab>
                    )}
                </Tabs>
            </div>
        </>
    );
}

function formatTotalCost(input = null) {
    return (
        <strong>
            £{Math.floor(input)}
            <sup>{(input % 1).toFixed(2).substring(1)}</sup>
        </strong>
    );
}
